import axios from "axios";

// Perjalanan Dinas (Fase 1) — pemanggilan API langsung (tanpa slice redux): modul ini
// berdiri sendiri, dan antrean Daftar Approval membaca data lewat helper yang sama.
const API_URL = process.env.REACT_APP_API_URL;

const headers = () => {
  let token = "";
  try {
    token = JSON.parse(localStorage.getItem("user") || "null")?.Token ?? "";
  } catch {
    token = "";
  }
  return { Authorization: "Bearer " + token, Accept: "*/*" };
};

// Pesan error dari backend (helpers.LogError → { message }) diteruskan apa adanya.
const pesanError = (e, fallback) =>
  e?.response?.data?.message || e?.response?.data?.Message || e?.message || fallback;

const unwrap = (promise, fallback) =>
  promise.then((r) => r.data).catch((e) => {
    throw new Error(pesanError(e, fallback));
  });

export const getPerjalananDinas = (search = "") =>
  unwrap(
    axios.get(API_URL + "hrd/perjalananDinas", { headers: headers(), params: { search } }),
    "Gagal memuat data"
  ).then((d) => d?.data ?? []);

export const getSpecPerjalananDinas = (id) => {
  const f = new FormData();
  f.append("TransaksiApprovalID", id);
  return unwrap(
    axios.post(API_URL + "hrd/perjalananDinasSpec", f, { headers: headers() }),
    "Gagal memuat detail"
  ).then((d) => (Array.isArray(d?.data) ? d.data[0] : null));
};

export const insertPerjalananDinas = (body) =>
  unwrap(axios.post(API_URL + "hrd/perjalananDinas", body, { headers: headers() }), "Gagal menyimpan");

export const updatePerjalananDinas = (body) =>
  unwrap(axios.put(API_URL + "hrd/perjalananDinas", body, { headers: headers() }), "Gagal menyimpan");

export const deletePerjalananDinas = (id) => {
  const f = new FormData();
  f.append("PerjalananDinasID", id);
  return unwrap(
    axios.post(API_URL + "hrd/perjalananDinas/delete", f, { headers: headers() }),
    "Gagal menghapus"
  );
};

export const getTarifUangSaku = () =>
  unwrap(axios.get(API_URL + "hrd/tarifUangSaku", { headers: headers() }), "Gagal memuat tarif").then(
    (d) => d?.data ?? []
  );

export const simpanTarifUangSaku = (items) =>
  unwrap(axios.put(API_URL + "hrd/tarifUangSaku", { Items: items }, { headers: headers() }), "Gagal menyimpan tarif");

export const rupiah = (n) =>
  new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(
    Number(n) || 0
  );

export const tglIndo = (ymd) => {
  if (!ymd) return "-";
  const [y, m, d] = String(ymd).slice(0, 10).split("-");
  return `${d}-${m}-${y}`;
};

// Komponen biaya sesuai template Form Biaya ("Uang Saku" tidak diinput — dihitung dari tarif jabatan).
export const JENIS_BIAYA = [
  "Tiket Perjalanan",
  "Transport PP Bandara",
  "Uang Makan",
  "Transport Lokal",
  "Hotel/Akomodasi",
  "Laundry",
  "Tol / Parkir",
  "Bagasi",
  "Lainnya",
];
export const MODA_TRANSPORTASI = ["Pesawat", "Mobil", "Kapal", "Bus", "Kereta", "Lainnya"];
export const METODE_PEMBAYARAN = [
  ["Transfer", "Transfer ke rekening karyawan"],
  ["Cash", "Cash"],
  ["CorporateCard", "Corporate Card"],
  ["Lainnya", "Lainnya"],
];

// ── Fase 2: perubahan agenda, laporan (LPD), settlement, tracker ──

const specPost = (path, id, fallback) => {
  const f = new FormData();
  f.append("TransaksiApprovalID", id);
  return unwrap(axios.post(API_URL + path, f, { headers: headers() }), fallback).then((d) =>
    Array.isArray(d?.data) ? d.data[0] : null
  );
};
const delPost = (path, key, id) => {
  const f = new FormData();
  f.append(key, id);
  return unwrap(axios.post(API_URL + path, f, { headers: headers() }), "Gagal menghapus");
};

export const getSpecUbah = (id) => specPost("hrd/perjalananDinas/ubahSpec", id, "Gagal memuat perubahan agenda");
export const insertUbah = (body) =>
  unwrap(axios.post(API_URL + "hrd/perjalananDinas/ubah", body, { headers: headers() }), "Gagal menyimpan");
export const updateUbah = (body) =>
  unwrap(axios.put(API_URL + "hrd/perjalananDinas/ubah", body, { headers: headers() }), "Gagal menyimpan");
export const deleteUbah = (id) => delPost("hrd/perjalananDinas/ubah/delete", "UbahID", id);

export const getSpecLaporan = (id) => specPost("hrd/perjalananDinas/laporanSpec", id, "Gagal memuat laporan");
export const insertLaporan = (body) =>
  unwrap(axios.post(API_URL + "hrd/perjalananDinas/laporan", body, { headers: headers() }), "Gagal menyimpan");
export const updateLaporan = (body) =>
  unwrap(axios.put(API_URL + "hrd/perjalananDinas/laporan", body, { headers: headers() }), "Gagal menyimpan");
export const deleteLaporan = (id) => delPost("hrd/perjalananDinas/laporan/delete", "LaporanID", id);
export const selesaikanSettlement = (LaporanID, Catatan) =>
  unwrap(
    axios.post(API_URL + "hrd/perjalananDinas/laporan/settlement", { LaporanID, Catatan }, { headers: headers() }),
    "Gagal menyelesaikan settlement"
  );

export const getOutstandingLaporan = (search = "") =>
  unwrap(
    axios.get(API_URL + "hrd/perjalananDinas/outstanding", { headers: headers(), params: { search } }),
    "Gagal memuat tracker"
  ).then((d) => d?.data ?? []);

// Bukti/kuitansi: diunggah ke file_upload service (pola File Karyawan/Lembur); backend hanya
// menyimpan nama file yang dikembalikan di sini.
export const uploadBukti = async (file) => {
  const { v4: uuidv4 } = require("uuid");
  const nama = uuidv4() + "-" + file.name.replaceAll(" ", "_");
  const fd = new FormData();
  fd.append("file", file, nama);
  try {
    await axios.post((process.env.REACT_APP_API_UPLOAD_URL || "/") + "upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  } catch (e) {
    throw new Error("Gagal mengunggah file bukti");
  }
  return nama;
};

export const unduhBukti = (namaFile) => {
  if (!namaFile) return;
  fetch((process.env.REACT_APP_API_UPLOAD_URL || "/") + "file/" + encodeURIComponent(namaFile), {
    headers: { Accept: "application/octet-stream" },
  })
    .then((r) => {
      if (!r.ok) throw new Error("HTTP " + r.status);
      return r.blob();
    })
    .then((blob) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = namaFile.replace(/^[0-9a-fA-F-]{36}-/, "");
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    })
    .catch(() => alert("File bukti tidak dapat diunduh"));
};

// Teks sisa hari kerja LPD: positif = sisa, negatif = terlambat.
export const teksSisaLPD = (d) =>
  d.SisaHariLPD < 0
    ? `Terlambat ${-d.SisaHariLPD} hari kerja`
    : d.SisaHariLPD === 0
    ? "Batas hari ini"
    : `${d.SisaHariLPD} hari kerja lagi`;

// Fase 3: ulangi pembuatan dokumen Finance (Jenis "UangMuka" + PerjalananDinasID | "Laporan" + LaporanID).
export const prosesUlangFinance = (Jenis, ID) =>
  unwrap(
    axios.post(API_URL + "hrd/perjalananDinas/finance/ulang", { Jenis, ID }, { headers: headers() }),
    "Gagal memproses dokumen Finance"
  );

// Surat Pengajuan: identitas read-only (Nama/NIK/Jabatan/Departemen) dan Form Biaya (tahap kedua).
export const getIdentitasPerjalananDinas = (nik) =>
  unwrap(
    axios.get(API_URL + "hrd/perjalananDinas/identitas", { headers: headers(), params: { nik } }),
    "Gagal memuat identitas karyawan"
  ).then((d) => d?.data ?? null);

export const simpanFormBiaya = (body) =>
  unwrap(axios.post(API_URL + "hrd/perjalananDinas/formBiaya", body, { headers: headers() }), "Gagal menyimpan Form Biaya");
